import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { z } from 'zod';
import View from './view';
import MiscUtils from 'services/MiscUtils';
import CPPUtils from 'services/CPPUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import PerformanceUtils from 'services/PerformanceUtils';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import Bugsnag from '@bugsnag/js';
import moment from 'moment-timezone';
import { runSavedPromptTyped } from 'thunks/runSavedPromptTyped';
import { useReduxForm } from 'hooks/useReduxForm';
import { useDefaultPharmacyState, useValidPreviousPharmacies } from 'hooks/useDefaultPharmacy';
import { useFormState } from 'react-hook-form';

const BLANK_TEMPLATE_NAME = 'Blank Prescription Template';
const EMPTY_ARRAY: any[] = [];
const EMPTY_OBJECT: Record<string, any> = {};

const newPrescriptionSchema = z.object({
  name: z.string().optional(),
  medications: z.array(z.any()).optional().default([]),
  additional_information: z.string().optional(),
  id: z.string().optional(),
  waiting_approval: z.string().optional().nullable(),
  prn: z.any().optional().nullable(),
  dosing_instructions: z.any().optional(),
  dosage: z.string().optional(),
  dosage_unit: z.string().optional(),
  route: z.string().optional(),
  quantity: z.string().optional(),
  quantity_unit: z.string().optional(),
  frequency: z.string().optional(),
  time: z.string().optional(),
  time_unit: z.string().optional(),
  originalPharmacy: z.any().optional().nullable(),
  note: z.string().optional(),
  refill: z.any().optional().nullable(),
  refills: z.string().optional(),
  attachments: z.array(z.any()).optional().default([]),
  drugbank_product: z.any().optional().nullable(),
  hide: z.boolean().optional().default(false),
  disable_refills: z.boolean().optional().default(false),
  comments: z.string().optional(),
});

type NewPrescriptionFormValues = z.infer<typeof newPrescriptionSchema>;

interface NewPrescriptionProps {
  patient: any;
  form?: string;
  onClose?: () => void;
  onSave?: () => void;
  showAISuggestions?: boolean;
  showCloseButton?: boolean;
  favorites?: any[];
  hideFavorites?: boolean;
  [key: string]: any;
}

const NewPrescription = (props: NewPrescriptionProps) => {
  const { patient, showAISuggestions = true } = props;
  const dispatch = useAppDispatch();
  const { pharmacy: defaultPharmacy, loading: defaultPharmacyLoading } =
    useDefaultPharmacyState(patient);

  const formName = '' + (props.form || 'NewPrescription');

  const authentication = useAppSelector((state) => state.authentication);
  const healthRecords = useAppSelector((state) => state.healthRecords.byId);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const templates = useAppSelector((state) => state.templates.templates) || EMPTY_ARRAY;

  const { control, watch, setValue, handleSubmit, reset, formState, getValues } =
    useReduxForm<NewPrescriptionFormValues>(formName, {
      destroyOnUnmount: false,
      defaultValues: {
        name: undefined,
        medications: [],
        additional_information: undefined,
        waiting_approval: null,
        prn: null,
        dosing_instructions: [],
        dosage: undefined,
        dosage_unit: undefined,
        route: undefined,
        quantity: undefined,
        quantity_unit: undefined,
        frequency: undefined,
        time: undefined,
        time_unit: undefined,
        originalPharmacy: null,
        note: undefined,
        refill: null,
        refills: undefined,
        attachments: [],
        drugbank_product: null,
        hide: false,
        disable_refills: false,
        comments: undefined,
      },
    });

  const { isValid } = useFormState({ control });

  const watchedValues = watch();
  const medications = watchedValues.medications || EMPTY_ARRAY;
  const originalPharmacy = watchedValues.originalPharmacy;
  const formAttachments = watchedValues.attachments || EMPTY_ARRAY;

  const [pharmacy, setPharmacy] = useState(originalPharmacy);
  const [pdfData, setPdfData] = useState<any>(undefined);
  const [loadingCrossTaper, setLoadingCrossTaper] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [blankTemplate, setBlankTemplate] = useState<any>();
  const [drugbankKey, setDrugbankKey] = useState(0);

  const [loadingAllergies, setLoadingAllergies] = useState<any>();
  const [matchingAllergies, setMatchingAllergies] = useState<Record<string, any>>({});

  const healthRecordData = healthRecords[patient.id] || EMPTY_OBJECT;
  const { prescriptions } = healthRecordData;

  // Recently-used pharmacies, filtered to those still in the current directory
  // (a stale RX-snapshot id won't preselect or bind to the patient correctly).
  const { pharmacies: previousPharmacies, loading: previousPharmaciesLoading } =
    useValidPreviousPharmacies(prescriptions, defaultPharmacy);

  const loadingPreview = useIsLoading(api.HealthRecords.previewPrescription);
  const loadingAdd = useIsLoading(api.HealthRecords.addPrescription);

  const allergies = healthRecordData.healthRecords?.allergies;
  const patientId = patient.id;

  useEffect(() => {
    const activeAllergies = allergies
      ?.filter((allergy: any) => CPPUtils.isHealthRecordItemEnabled(allergy))
      .map((allergy: any) => MiscUtils.cleanObject(allergy));

    const missingMedications = medications.filter(
      (medication: any) => matchingAllergies[medication.id] === undefined,
    );

    let newMatchingAllergies = { ...matchingAllergies };
    Object.keys(newMatchingAllergies).forEach((matchingId) => {
      if (!medications.find(({ id }: any) => matchingId === id)) {
        delete newMatchingAllergies[matchingId];
      }
    });
    if (missingMedications?.length > 0 && activeAllergies?.length > 0) {
      setLoadingAllergies(missingMedications.map(({ id }: any) => id));

      runSavedPromptTyped(
        'Drug Allergy Checker v2',
        {
          drugs: JSON.stringify(MiscUtils.cleanObject(missingMedications)),
          allergies: JSON.stringify(
            activeAllergies.map((allergy: any) => ({
              id: allergy.id,
              value: allergy.value,
            })),
          ),
        },
        {
          patientId: patient.id,
        },
      )
        .then((result: any) => {
          if (result) {
            try {
              if (Array.isArray(result)) {
                missingMedications.forEach((medication: any) => {
                  if (!medication.id) {
                    return;
                  }
                  newMatchingAllergies[medication.id] =
                    result.filter((r: any) => r.drug_id === medication.id && r.matched) || [];
                });
                setMatchingAllergies(newMatchingAllergies);
              }
            } catch (err: any) {
              Bugsnag.notify(err);
            }
          }
        })
        .finally(() => {
          setLoadingAllergies(undefined);
        });
    } else {
      if (Object.keys(newMatchingAllergies).length !== Object.keys(matchingAllergies).length) {
        setMatchingAllergies(newMatchingAllergies);
      }
    }
  }, [dispatch, patientId, allergies, medications, JSON.stringify(matchingAllergies)]);

  useEffect(() => {
    if (originalPharmacy) {
      setPharmacy(originalPharmacy);
    }
  }, [originalPharmacy]);

  useEffect(() => {
    let value = CPPUtils.calculateMedicationQuantity({
      time: watchedValues.time,
      time_unit: watchedValues.time_unit,
      prn: watchedValues.prn,
      frequency: watchedValues.frequency,
      route: watchedValues.route,
      dosage: watchedValues.dosage,
      dosage_unit: watchedValues.dosage_unit,
      dosing_instructions: watchedValues.dosing_instructions,
    });
    if (value) {
      setValue('quantity', '' + value);
      setValue('quantity_unit', 'tabs');
    }
  }, [
    watchedValues.time,
    watchedValues.time_unit,
    watchedValues.prn,
    watchedValues.frequency,
    watchedValues.route,
    watchedValues.dosage,
    watchedValues.dosage_unit,
    watchedValues.dosing_instructions,
  ]);

  useEffect(() => {
    if (!patient || !!originalPharmacy || !!pharmacy) return;
    // Wait for the default pharmacy and the recent-pharmacy validation to resolve
    // before preselecting. Otherwise we race the async lookups and lock in a
    // fallback before the real values arrive (the `!!pharmacy` guard then blocks
    // the update).
    if (defaultPharmacyLoading || previousPharmaciesLoading) return;
    // Prefer the patient's default pharmacy; fall back to the most recently used
    // one (already filtered to pharmacies still in the directory) when there is
    // no default on file (or it was removed from the directory).
    let preselected = defaultPharmacy ?? previousPharmacies[0];
    setPharmacy(preselected);
    setValue('originalPharmacy', preselected);
  }, [
    previousPharmacies,
    previousPharmaciesLoading,
    patient,
    originalPharmacy,
    pharmacy,
    defaultPharmacy,
    defaultPharmacyLoading,
  ]);

  const templatesFetchedRef = useRef(false);
  useEffect(() => {
    if (!templatesFetchedRef.current && templates.length === 0) {
      templatesFetchedRef.current = true;
      dispatch(api.Templates.getDocumentTemplates());
    }

    if (!blankTemplate) {
      let template = templates.find(
        (template: any) => template.description === BLANK_TEMPLATE_NAME,
      );
      setBlankTemplate(template);
    }
  }, [blankTemplate, templates, dispatch]);

  function selectProduct(name: string | null, product: any) {
    setValue('name', name || '');
    setValue('drugbank_product', product);
  }

  function addMedication(values: any) {
    let medication: any = {
      id: uuidV4(),
      prn: values.prn,
      dosing_instructions: values.dosing_instructions,
      comments: values.comments,
      dosage: values.dosage,
      dosage_unit: values.dosage_unit,
      frequency: values.frequency,
      name: values.name,
      quantity: values.quantity,
      quantity_unit: values.quantity_unit,
      refills: values.refills,
      route: values.route,
      time: values.time,
      time_unit: values.time_unit,
      drugbank_product: values.drugbank_product
        ? cleanDrugbankProduct(values.drugbank_product)
        : undefined,
      disable_refills: values.disable_refills,
    };

    if (!medication.prn) delete medication.prn;

    let newMedications = [...(medications || []), medication];
    clearMedication(newMedications);
    setDrugbankKey(drugbankKey + 1);
  }

  function clearMedication(newMedications?: any[]) {
    // Clears only the current working drug fields. The already-added
    // "Medications Prescribed" list (and other prescription-level context) is
    // preserved by re-seeding it below, whether or not a caller passes an
    // updated list. Use clearMedications()/setValue('medications', []) to wipe
    // the added list.
    const currentValues = getValues();
    reset(
      {
        prn: null,
        id: currentValues.id,
        medications: newMedications || medications || [],
        additional_information: currentValues.additional_information,
        refill: currentValues.refill,
        attachments: formAttachments,
        name: '',
        waiting_approval: null,
        dosing_instructions: [],
        dosage: '',
        dosage_unit: '',
        route: '',
        quantity: '',
        quantity_unit: '',
        frequency: '',
        time: '',
        time_unit: '',
        originalPharmacy: currentValues.originalPharmacy,
        note: '',
        refills: '',
        drugbank_product: null,
        hide: false,
        disable_refills: false,
        comments: '',
      },
      { keepDefaultValues: true },
    );
    setDrugbankKey(drugbankKey + 1);
  }

  function deleteMedication(item: any) {
    let newMedications = (medications || []).filter((medication: any) => {
      return medication !== item;
    });
    setValue('medications', newMedications);
  }

  function editMedication(item: any) {
    clearMedication();
    deleteMedication(item);
    for (let key in item) {
      if (key !== 'id') {
        setValue(key as keyof NewPrescriptionFormValues, item[key]);
      }
    }
  }

  async function printPrescription(prescription: any) {
    let result = await dispatch(
      api.HealthRecords.getPrescriptionAttachment({
        patientId: prescription.patient_id,
        prescriptionId: prescription.id,
        resource: {
          url: prescription.attachments[0],
        },
      }),
    );
    if (result) {
      let blobUrl = MiscUtils.getBlobUrl(result);
      if (blobUrl) {
        MiscUtils.printUrl(blobUrl);
      }
    }
  }

  async function addPrescriptionWrapper(fax = false, print = false) {
    const currentRefill = getValues().refill;
    let prescription = preparePrescription();
    setOpenPreviewModal(false);
    let result = await dispatch(api.HealthRecords.addPrescription({ prescription, fax: !!fax }), {
      title: 'Prescription',
      successText: 'Prescription added successfully!',
    });
    if (result) {
      reset();
      dispatch(api.HealthRecords.getCPP({ patientId: prescription.patient_id })).then(() => {
        if (currentRefill) {
          dispatch(
            api.HealthRecords.editPrescriptionRefill({
              id: currentRefill.id,
              patientId: currentRefill.patient_id,
              prescriptionRefill: { ...currentRefill, status: 'completed' },
            }),
          );
        }
      });

      if (print) {
        printPrescription(result).catch((err: any) => {
          console.log(err);
        });
      }
      props.onSave?.();
    }
  }

  async function previewPrescriptionWrapper() {
    let prescription = preparePrescription();
    let result = await dispatch(api.HealthRecords.previewPrescription({ prescription }), {
      errorTitle: 'Prescription',
    });

    if (result) {
      let url = MiscUtils.getBlobUrl(result);
      setPdfData({ url, type: result.type });
      setOpenPreviewModal(true);
    }
  }

  // Select a pharmacy for THIS prescription. Kept separate from the patient's
  // saved default (default_pharmacy_id) — that is only changed via an explicit
  // "Save as default" action so a one-off pick can't silently re-route every
  // future Rx.
  function onSelectPharmacy(selected: any) {
    setPharmacy(selected);
    setValue('originalPharmacy', selected);
  }

  // Clears the pharmacy for this prescription only. Intentionally does NOT touch
  // the patient's saved default pharmacy.
  function removePharmacy() {
    if (pharmacy) {
      setPharmacy({});
      setValue('originalPharmacy', null);
    }
  }

  function isIntern() {
    let physician = physicians[authentication.user!.id!] || authentication.user;
    return !!(physician as any).intern_of;
  }

  function preparePrescription() {
    let selectedPharmacy = !!pharmacy?.name ? pharmacy : undefined;

    let physician = physicians[authentication.user!.id!] || authentication.user;
    let waiting_approval = (physician as any).intern_of ? (physician as any).intern_of : undefined;

    return {
      id: watchedValues.id,
      attachments: watchedValues.attachments || [],
      medications: medications || [],
      pharmacy: selectedPharmacy,
      patient_id: patient.id,
      physician_id: (authentication as any).decodedToken.sub,
      additional_information: watchedValues.additional_information,
      hide: watchedValues.hide,
      date: moment().unix(),
      waiting_approval,
    };
  }

  async function onCrossTaper(data: any) {
    setLoadingCrossTaper(true);
    const result = await runSavedPromptTyped(
      'Medication Cross-taper',
      {
        inputs: JSON.stringify(data),
      },
      { patientId: patient.id },
    );
    setLoadingCrossTaper(false);
    if (result) setValue('additional_information', result as string);
  }

  return (
    <View
      {...props}
      {...{
        previewPrescription: previewPrescriptionWrapper,
        addPrescription: addPrescriptionWrapper,
        setPharmacy,
        removePharmacy,
        onSelectPharmacy,
        addMedication,
        deleteMedication,
        clearMedication,
        editMedication,
        setOpenPreviewModal,
        selectProduct,
        drugbankKey,
        onCrossTaper,
        showAISuggestions,
      }}
      {...{
        isIntern: isIntern(),
        previousPharmacies,
        matchingAllergies,
        loadingAllergies,
        pharmacy,
        pdfData,
        loadingPreview,
        loadingAdd,
        medications,
        openPreviewModal,
        blankTemplate,
        loadingCrossTaper,
      }}
      // RHF props
      control={control}
      watch={watch}
      setValue={setValue}
      handleSubmit={handleSubmit}
      reset={reset}
      formState={formState}
      getValues={getValues}
      // Redux state previously from mapStateToProps
      authentication={authentication}
      healthRecords={healthRecords}
      physicians={physicians}
      templates={templates}
      formAttachments={formAttachments}
      // Watched form values
      name={watchedValues.name}
      additional_information={watchedValues.additional_information}
      id={watchedValues.id}
      waiting_approval={watchedValues.waiting_approval}
      prn={watchedValues.prn}
      dosing_instructions={watchedValues.dosing_instructions}
      dosage={watchedValues.dosage}
      dosage_unit={watchedValues.dosage_unit}
      route={watchedValues.route}
      quantity={watchedValues.quantity}
      quantity_unit={watchedValues.quantity_unit}
      frequency={watchedValues.frequency}
      time={watchedValues.time}
      time_unit={watchedValues.time_unit}
      originalPharmacy={originalPharmacy}
      note={watchedValues.note}
      refill={watchedValues.refill}
      refills={watchedValues.refills}
      drugbank_product={watchedValues.drugbank_product}
      hide={watchedValues.hide}
      disable_refills={watchedValues.disable_refills}
      comments={watchedValues.comments}
      isValid={isValid}
    />
  );
};

export function cleanDrugbankProduct(drugbank_product: any = {}) {
  let copy = PerformanceUtils.clone(drugbank_product);
  delete copy.synonyms;
  delete copy.mesh_tree_numbers;
  delete copy.therapeutic_categories;
  return copy;
}

export default NewPrescription;
